<template>
  <v-form
    ref="form"
    :disabled="disabled"
  >
    <!-- Top edit button -->
    <avs-editInfoButton
      v-if="isEditable"
      :isEnabled="!disabled"
      @click="$event => setEditable(disabled)"
    />

    <!-- DISABLED FIELDS: These fields are generated by db only for info purpose -->
    <v-row class="mt-8">
      <v-col
        cols="6"
        class="my-0 py-0"
      >
        <v-text-field
          v-if="document.created_by"
          :value="document.created_by"
          :label="$t('commons.created_by')"
          dense
          disabled
        ></v-text-field>
      </v-col>
      <v-col
        cols="6"
        class="my-0 py-0"
      >
        <v-text-field
          v-if="document.created_at"
          :value="toDatetime(document.created_at)"
          :label="$t('commons.created_at')"
          dense
          disabled
        ></v-text-field>
      </v-col>
      <v-col
        cols="6"
        class="my-0 py-0"
      >
        <v-text-field
          v-if="document.updated_by"
          :value="document.updated_by"
          :label="$t('commons.updated_by')"
          dense
          disabled
        ></v-text-field>
      </v-col>
      <v-col
        cols="6"
        class="my-0 py-0"
      >
        <v-text-field
          v-if="document.updated_at"
          :value="toDatetime(document.updated_at)"
          :label="$t('commons.updated_at')"
          dense
          disabled
        ></v-text-field>
      </v-col>
    </v-row>

    <v-divider class="my-6"></v-divider>

    <!-- EDITABLE FIELDS -->

    <v-text-field
      v-model="document.name"
      :enabled="enabledFields.name"
      :rules="rules.name"
      @input="newVal => handleInput({ name: newVal })"
      :label="$t('commons.name')"
      counter="64"
      maxlength="64"
      type="text"
      clearable
      dense
      outlined
    ></v-text-field>

    <ProjectSelect
      v-model="document.project"
      :enabled="enabledFields.project"
      :rules="rules.project"
      @input="newVal => handleInput({ project: newVal })"
    />

    <TemplateSelect
      v-model="document.template"
      :companyId="companyId"
      :enabled="enabledFields.template"
      :rules="rules.template"
      @input="newVal => handleInput({ template: newVal })"
    />

    <div v-if="isEditable && !disabled">
      <v-col class="d-flex justify-end gap-4">
        <avs-saveButton @click="handleSave" />
        <avs-cancelButton @click="handleCancel" />
      </v-col>
    </div>
  </v-form>
</template>

<script>
import { toDatetime } from "@/utils/datetime"
import { isEqual } from "@/utils/object"
import { required, max64 } from "@/utils/validator"

import ProjectSelect from "@/components/inputs/ProjectSelect.vue"
import TemplateSelect from "@/components/inputs/TemplateSelect.vue"

export default {
  name: "DocumentForm",
  components: { TemplateSelect, ProjectSelect },
  props: {
    fields: {
      type: Object,
      default: () => ({
        name: true,
        project: true,
        template: true
      })
    },
    isEditable: {
      type: Boolean,
      default: true
    },
    performSave: {
      type: Function,
      required: true
    },
    value: {
      type: Object,
      required: true
    }
  },
  computed: {
    companyId() {
      return this.document.project?.company.pk
    },
    enabledFields() {
      const enabled = !(this.readonly || this.disabled)
      return {
        name: enabled && this.fields.name,
        project: enabled && this.fields.project,
        template: enabled && this.fields.template
      }
    }
  },
  data() {
    return {
      disabled: true,
      document: {},
      rules: {
        name: [required, max64],
        project: [required],
        template: [required]
      }
    }
  },
  methods: {
    toDatetime,
    setEditable(val = true) {
      this.disabled = !val
    },
    handleCancel() {
      this.setEditable(false)
      this.document = Object.assign({}, this.value)
      this.$emit("cancel")
    },
    handleInput(partialUpdate) {
      this.document = { ...this.document, ...partialUpdate }
    },
    handleSave() {
      if (this.$refs.form.validate()) {
        if (isEqual(this.document, this.value)) {
          // Objects are equal, no changes to perform
          this.$emit("success", false)
          this.setEditable(false)
        } else {
          this.performSave(this.document)
            .then(response => {
              this.$emit("input", response)
              this.$emit("success", true)
              this.setEditable(false)
            })
            .catch(error => {
              const message = error?.response.data
              this.$emit("error", message)
            })
        }
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newValue, oldValue) {
        const isObj = Object.keys(newValue).length > 0
        this.setEditable(!isObj)
        if (isObj) {
          this.document = Object.assign({}, newValue)
        }
      }
    }
  }
}
</script>
